import { Injectable } from "@angular/core";
import { Observable, map, of, tap, throwError } from "rxjs";
import { BaseHandler } from "@app/core/shared/data/network/base-handler";
import { ApiUrls } from "@app/core/shared/data/network/api-urls";
import {
  HandlerBuilder,
  HttpMethod,
} from "@app/core/shared/data/network/base-handler.parameters";
import { TokenAuthorization } from "@app/core/shared/data/network/token-authorization";
import { TokenRepo } from "@app/core/shared/domain/token.repo";
import { MissingCredentialsError } from "@app/core/shared/domain/missing-credentials.error";
import { CommunityClub } from "@app/core/community/domain/model/community-club";
import {
  CommunityClubsQuery,
  CommunityClubsPage,
} from "@app/core/community/domain/community.repo";
import {
  CommunityClubApiResponse,
  CommunityClubListApiResponse,
  ClubFavoriteResponseApiResponse,
} from "@app/core/shared/domain/api-types-filtered";

function mapClub(data: CommunityClubApiResponse): CommunityClub {
  return new CommunityClub(
    data.id,
    data.name,
    data.city,
    data.country,
    "",
    data.address,
    data.image_url,
    data.cover_image_url,
    0,
    0,
    data.members_count ?? null,
    data.courts_count ?? null,
    data.distance_km,
    false,
    false,
    "",
    null,
    "",
    [],
    (data.courts_aiball_count ?? 0) > 0,
    data.latitude,
    data.longitude,
    data.phone,
    data.website,
    data.google_maps_link,
    [],
    data.all_courts_sacra_active,
    data.is_favorite
  );
}

const CLUBS_CACHE_TTL_MS = 5 * 60 * 1000;

interface ClubsCache {
  data: CommunityClubsPage;
  cachedAt: number;
}

@Injectable({ providedIn: "root" })
export class CommunityWS {
  constructor(
    private baseHandler: BaseHandler,
    private tokenRepo: TokenRepo
  ) {}

  private clubsCache = new Map<string, ClubsCache>();

  private cacheKey(query: CommunityClubsQuery): string {
    return JSON.stringify({
      s: query.search,
      lat: query.lat != null ? Math.round(query.lat * 1000) / 1000 : null,
      lng: query.lng != null ? Math.round(query.lng * 1000) / 1000 : null,
      sort: query.sortBy ?? null,
      filter: query.filter ?? null,
      all: query.showAll ?? false,
    });
  }

  private isCacheable(query: CommunityClubsQuery): boolean {
    return (
      !query.search &&
      !query.sortBy &&
      !query.filter &&
      (!query.page || query.page === 1)
    );
  }

  getClubs(query: CommunityClubsQuery): Observable<CommunityClubsPage> {
    const cacheable = this.isCacheable(query);
    if (cacheable) {
      const key = this.cacheKey(query);
      const cached = this.clubsCache.get(key);
      if (cached && Date.now() - cached.cachedAt < CLUBS_CACHE_TTL_MS) {
        return of(cached.data);
      }
    }

    const token = this.tokenRepo.get();
    if (token == null) {
      return throwError(() => new MissingCredentialsError());
    }

    const urlParams: Record<string, string | number> = {};
    if (query.search) urlParams["search"] = query.search;
    if (query.lat != null) {
      urlParams["lat"] = query.lat;
      // Manual location uses an oversized radius so the backend returns every
      // club; the frontend then handles the "nearby vs all" filtering itself.
      urlParams["radius_km"] = query.manualLocation
        ? 1000000
        : query.showAll
          ? 99999
          : 200;
    }
    if (query.lng != null) urlParams["lng"] = query.lng;
    if (query.sortBy) urlParams["sort_by"] = query.sortBy;
    if (query.filter && query.filter !== "ALL")
      urlParams["filter"] = query.filter;
    if (query.page && query.page > 1) urlParams["page"] = query.page;
    if (query.pageSize) urlParams["page_size"] = query.pageSize;
    if (query.onlyFavorites) urlParams["only_favorites"] = "true";

    const builder = new HandlerBuilder()
      .setUrl(ApiUrls.GET_COMMUNITY_CLUBS_ENDPOINT())
      .setMethod(HttpMethod.get)
      .setAuthorization(new TokenAuthorization(token));
    if (Object.keys(urlParams).length > 0) {
      builder.setUrlParams(urlParams);
    }
    return this.baseHandler.execute(builder.build()).pipe(
      map((response: CommunityClubListApiResponse) => ({
        clubs: response.results.map(mapClub),
        total: response.total,
      })),
      tap((page) => {
        if (cacheable) {
          this.clubsCache.set(this.cacheKey(query), {
            data: page,
            cachedAt: Date.now(),
          });
        }
      })
    );
  }

  getClubById(id: string): Observable<CommunityClub> {
    const token = this.tokenRepo.get();
    if (token == null) {
      return throwError(() => new MissingCredentialsError());
    }
    const parameters = new HandlerBuilder()
      .setUrl(ApiUrls.GET_COMMUNITY_CLUB_DETAIL_ENDPOINT(id))
      .setMethod(HttpMethod.get)
      .setAuthorization(new TokenAuthorization(token))
      .build();
    return this.baseHandler
      .execute(parameters)
      .pipe(map((data: CommunityClubApiResponse) => mapClub(data)));
  }

  toggleFavorite(id: string, isFavorite: boolean): Observable<boolean> {
    const token = this.tokenRepo.get();
    if (token == null) {
      return throwError(() => new MissingCredentialsError());
    }
    const parameters = new HandlerBuilder()
      .setUrl(ApiUrls.TOGGLE_COMMUNITY_CLUB_FAVORITE_ENDPOINT(id))
      .setMethod(HttpMethod.post)
      .setAuthorization(new TokenAuthorization(token))
      .setBody({ is_favorite: isFavorite })
      .build();
    return this.baseHandler
      .execute(parameters)
      .pipe(map((data: ClubFavoriteResponseApiResponse) => data.is_favorite));
  }
}
